/**
 * ADPCM Audio Player for Client
 *
 * Decodes and plays ADPCM audio packets received from the server.
 * Uses Web Audio API for playback with optional retro filtering.
 */

// =============================================================================
// IMA ADPCM Tables (same as server-side)
// =============================================================================

const STEP_TABLE: number[] = [
  7, 8, 9, 10, 11, 12, 13, 14,
  16, 17, 19, 21, 23, 25, 28, 31,
  34, 37, 41, 45, 50, 55, 60, 66,
  73, 80, 88, 97, 107, 118, 130, 143,
  157, 173, 190, 209, 230, 253, 279, 307,
  337, 371, 408, 449, 494, 544, 598, 658,
  724, 796, 876, 963, 1060, 1166, 1282, 1411,
  1552, 1707, 1878, 2066, 2272, 2499, 2749, 3024,
  3327, 3660, 4026, 4428, 4871, 5358, 5894, 6484,
  7132, 7845, 8630, 9493, 10442, 11487, 12635, 13899,
  15289, 16818, 18500, 20350, 22385, 24623, 27086, 29794,
  32767
];

const INDEX_TABLE: number[] = [
  -1, -1, -1, -1, 2, 4, 6, 8,
  -1, -1, -1, -1, 2, 4, 6, 8
];

// =============================================================================
// ADPCM Decoder
// =============================================================================

interface DecoderState {
  predictor: number;
  stepIndex: number;
}

class ADPCMDecoder {
  private state: DecoderState = { predictor: 0, stepIndex: 0 };

  private decodeSample(nibble: number): number {
    const step = STEP_TABLE[this.state.stepIndex];

    let diff = 0;
    if (nibble & 4) diff += step;
    if (nibble & 2) diff += step >> 1;
    if (nibble & 1) diff += step >> 2;
    diff += step >> 3;

    if (nibble & 8) {
      this.state.predictor -= diff;
    } else {
      this.state.predictor += diff;
    }

    if (this.state.predictor > 32767) this.state.predictor = 32767;
    if (this.state.predictor < -32768) this.state.predictor = -32768;

    this.state.stepIndex += INDEX_TABLE[nibble];
    if (this.state.stepIndex < 0) this.state.stepIndex = 0;
    if (this.state.stepIndex > 88) this.state.stepIndex = 88;

    return this.state.predictor;
  }

  decodeWithHeader(packet: Uint8Array): Float32Array {
    if (packet.length < 3) {
      return new Float32Array(0);
    }

    // Read header
    const predictor = (packet[0] | (packet[1] << 8)) << 16 >> 16;
    const stepIndex = Math.min(88, Math.max(0, packet[2]));

    this.state.predictor = predictor;
    this.state.stepIndex = stepIndex;

    // Decode samples
    const adpcmData = packet.subarray(3);
    const numSamples = adpcmData.length * 2;
    const output = new Float32Array(numSamples);

    let outIdx = 0;
    for (let i = 0; i < adpcmData.length; i++) {
      const byte = adpcmData[i];
      // High nibble first
      output[outIdx++] = this.decodeSample((byte >> 4) & 0x0F) / 32768;
      // Low nibble
      if (outIdx < numSamples) {
        output[outIdx++] = this.decodeSample(byte & 0x0F) / 32768;
      }
    }

    return output;
  }

  reset(): void {
    this.state.predictor = 0;
    this.state.stepIndex = 0;
  }
}

// =============================================================================
// ADPCM Player
// =============================================================================

export interface ADPCMPlayerConfig {
  sampleRate: number;      // Expected sample rate of decoded audio
  bufferSize: number;      // Number of packets to buffer before playing
  retroFilter: boolean;    // Apply retro low-pass filter
  volume: number;          // 0.0 - 1.0
}

const DEFAULT_CONFIG: ADPCMPlayerConfig = {
  sampleRate: 11025,
  bufferSize: 3,
  retroFilter: true,
  volume: 0.7,
};

export class ADPCMPlayer {
  private ctx: AudioContext | null = null;
  private decoder: ADPCMDecoder;
  private config: ADPCMPlayerConfig;

  // Audio processing
  private gainNode: GainNode | null = null;
  private filterNode: BiquadFilterNode | null = null;

  // Packet buffering for smooth playback
  private packetBuffer: Float32Array[] = [];
  private isPlaying: boolean = false;
  private nextPlayTime: number = 0;

  // Stats
  private packetsReceived: number = 0;
  private packetsPlayed: number = 0;
  private underruns: number = 0;

  constructor(config: Partial<ADPCMPlayerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.decoder = new ADPCMDecoder();
  }

  /**
   * Initialize with an existing AudioContext
   * @param audioContext - AudioContext to use
   * @param masterOutput - Optional node to route output through (for shared volume control)
   */
  initialize(audioContext: AudioContext, masterOutput?: AudioNode): void {
    this.ctx = audioContext;

    // Create gain node for volume control
    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = this.config.volume;

    const outputNode = masterOutput || this.ctx.destination;

    // Create low-pass filter for retro sound (optional)
    if (this.config.retroFilter) {
      this.filterNode = this.ctx.createBiquadFilter();
      this.filterNode.type = 'lowpass';
      this.filterNode.frequency.value = 4000;  // Cut highs for retro feel
      this.filterNode.Q.value = 0.7;

      this.filterNode.connect(this.gainNode);
      this.gainNode.connect(outputNode);
    } else {
      this.gainNode.connect(outputNode);
    }

    this.nextPlayTime = this.ctx.currentTime;
    console.log('ADPCM player initialized');
  }

  /**
   * Receive and decode an ADPCM packet
   */
  receivePacket(packetData: Uint8Array): void {
    if (!this.ctx) return;

    this.packetsReceived++;

    // Decode packet
    const samples = this.decoder.decodeWithHeader(packetData);
    if (samples.length === 0) return;

    // Add to buffer
    this.packetBuffer.push(samples);

    // Start playing if we have enough buffered
    if (!this.isPlaying && this.packetBuffer.length >= this.config.bufferSize) {
      this.isPlaying = true;
      this.schedulePlayback();
    }
  }

  /**
   * Schedule buffered packets for playback
   */
  private schedulePlayback(): void {
    if (!this.ctx || !this.gainNode) return;

    const now = this.ctx.currentTime;

    // Catch up if we've fallen behind
    if (this.nextPlayTime < now) {
      this.underruns++;
      this.nextPlayTime = now;
    }

    // Schedule all buffered packets
    while (this.packetBuffer.length > 0) {
      const samples = this.packetBuffer.shift()!;

      // Create audio buffer
      const buffer = this.ctx.createBuffer(1, samples.length, this.config.sampleRate);
      buffer.getChannelData(0).set(samples);

      // Create source and schedule
      const source = this.ctx.createBufferSource();
      source.buffer = buffer;

      if (this.config.retroFilter && this.filterNode) {
        source.connect(this.filterNode);
      } else {
        source.connect(this.gainNode!);
      }

      source.start(this.nextPlayTime);
      this.packetsPlayed++;

      // Calculate duration and schedule next
      const duration = samples.length / this.config.sampleRate;
      this.nextPlayTime += duration;
    }

    // Check for more packets soon
    if (this.isPlaying) {
      setTimeout(() => this.schedulePlayback(), 20);
    }
  }

  /**
   * Set volume (0.0 - 1.0)
   */
  setVolume(volume: number): void {
    this.config.volume = Math.max(0, Math.min(1, volume));
    if (this.gainNode) {
      this.gainNode.gain.value = this.config.volume;
    }
  }

  /**
   * Enable/disable retro filter
   */
  setRetroFilter(enabled: boolean): void {
    this.config.retroFilter = enabled;
    // Note: Requires re-initialization to take effect
  }

  /**
   * Stop playback and clear buffers
   */
  stop(): void {
    this.isPlaying = false;
    this.packetBuffer = [];
    this.decoder.reset();
    if (this.ctx) {
      this.nextPlayTime = this.ctx.currentTime;
    }
  }

  /**
   * Get playback statistics
   */
  getStats(): {
    packetsReceived: number;
    packetsPlayed: number;
    bufferSize: number;
    underruns: number;
    isPlaying: boolean;
  } {
    return {
      packetsReceived: this.packetsReceived,
      packetsPlayed: this.packetsPlayed,
      bufferSize: this.packetBuffer.length,
      underruns: this.underruns,
      isPlaying: this.isPlaying,
    };
  }

  /**
   * Check if player is initialized
   */
  isInitialized(): boolean {
    return this.ctx !== null;
  }
}

// =============================================================================
// Factory Function
// =============================================================================

export function createADPCMPlayer(config?: Partial<ADPCMPlayerConfig>): ADPCMPlayer {
  return new ADPCMPlayer(config);
}
